import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { addMonths, endOfMonth, startOfMonth, subMonths } from "date-fns";
import { type FunctionComponent, memo } from "react";

import type { ButtonVariantsProps } from "~/components/buttons/Button/buttonVariants.ts";
import { buttonVariants } from "~/components/buttons/Button/buttonVariants.ts";
import type { FormInputBaseVariants } from "~/components/formElements/formInputBaseVariant.ts";
import useOnClickOutside from "~/hooks/useOnClickOutside.ts";
import DeliveryPeriodPickerPopover
	from "~/modules/billing/components/DeliveryPeriodFilter/components/DeliveryPeriodPickerPopover.tsx";
import type { DateRange } from "~/types/dateAndTimeTypes.ts";
import { formatSmartDateRange } from "~/utils/dateAndTimeUtils.ts";

import { computeDateRange, type TempDateRange, useDeliveryPeriodPicker } from "./useDeliveryPeriodPicker.ts";

type Props = ButtonVariantsProps & FormInputBaseVariants & {
	selectedPeriod: DateRange;
	onChange: (period: DateRange) => void;
};

const RequiredDeliveryPeriodFilter: FunctionComponent<Props> = memo(({
	selectedPeriod,
	onChange,
}) => {
	const startDate = selectedPeriod.startDate;
	const endDate = selectedPeriod.endDate;

	const {
		isOpen,
		setIsOpen,
		refs,
		floatingStyles,
		context,
		getReferenceProps,
		getFloatingProps,
		tempDateRange,
		setTempDateRange,
		quickSelectOptions,
		activeQuickSelect,
		now,
	} = useDeliveryPeriodPicker({ startDate, endDate });

	const handleRangeChange = (value: TempDateRange) => {
		const [tempStart, tempEnd] = value;
		setTempDateRange(value);
		const computed = computeDateRange(tempStart, tempEnd);
		// Only call onChange if we have valid dates
		if (computed.startDate && computed.endDate) {
			onChange({ startDate: computed.startDate, endDate: computed.endDate });
		}
	};

	const handleAdvanceMonthClick = () => {
		const currentStartDate = startDate ? new Date(startDate) : now;
		const currentEndDate = endDate ? new Date(endDate) : now;
		const newStartDate = startOfMonth(addMonths(currentStartDate, 1));
		const newEndDate = endOfMonth(addMonths(currentEndDate, 1));

		onChange({ startDate: newStartDate, endDate: newEndDate });
	};

	const handleRewindMonthClick = () => {
		const currentStartDate = startDate ? new Date(startDate) : now;
		const currentEndDate = endDate ? new Date(endDate) : now;
		const newStartDate = startOfMonth(subMonths(currentStartDate, 1));
		const newEndDate = endOfMonth(subMonths(currentEndDate, 1));

		onChange({ startDate: newStartDate, endDate: newEndDate });
	};

	const applyFilter = () => {
		const [tempStart, tempEnd] = tempDateRange;
		const computed = computeDateRange(tempStart, tempEnd);
		// Only call onChange if we have valid dates
		if (computed.startDate && computed.endDate) {
			onChange({ startDate: computed.startDate, endDate: computed.endDate });
		}
		setIsOpen(false);
	};

	const handlePopoverClose = () => {
		applyFilter();
	};

	useOnClickOutside(refs.floating, handlePopoverClose);

	return (
		<>
			<div className="relative flex items-center justify-between">
				<button onClick={handleRewindMonthClick}
						className={clsx(buttonVariants({ theme: "primary", size: "auto"}),"rounded-r-none px-1 py-0.5")}
						type="button">
					<ChevronLeftIcon className="size-6" />
				</button>
				<button onClick={handleAdvanceMonthClick}
						className={clsx(buttonVariants({ theme: "primary", size: "auto",  }), "ml-0.5 mr-3 rounded-l-none px-1 py-0.5")}
						type="button">
					<ChevronRightIcon className="size-6" />
				</button>
				<button
					ref={refs.setReference}
					{...getReferenceProps({ onClick: (e) => e.stopPropagation() })}
				>
					<span className="text-3xl font-bold text-primary-500 hover:underline">{formatSmartDateRange(startDate, endDate, {
						month: "MMMM",
						year: " yyyy",
					})}</span>
				</button>

			</div>

			<DeliveryPeriodPickerPopover
				isOpen={isOpen}
				context={context}
				refs={refs}
				floatingStyles={floatingStyles}
				getFloatingProps={getFloatingProps}
				tempDateRange={tempDateRange}
				onRangeChange={handleRangeChange}
				quickSelectOptions={quickSelectOptions}
				activeQuickSelect={activeQuickSelect}
				now={now}
			/>
		</>
	);
});

RequiredDeliveryPeriodFilter.displayName = "RequiredDeliveryPeriodFilter";

export default RequiredDeliveryPeriodFilter;